import { useCallback } from 'react'
import { supabase } from '../supabase'

// Reusable hook for customer flag checks
export function useCustomerFlags(sellerId) {

  const checkPhone = useCallback(async (phone) => {
    if (!phone || !sellerId) return { privateFlag: null, communityFlag: null }

    // Check private flag (this seller flagged this phone)
    const { data: privateFlag } = await supabase
      .from('customer_flags')
      .select('*')
      .eq('flagged_by_seller_id', sellerId)
      .eq('phone', phone)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()

    // Check community flag (3+ sellers)
    const { data: communityFlag } = await supabase
      .from('community_flags')
      .select('*')
      .eq('phone', phone)
      .single()

    return {
      privateFlag: privateFlag || null,
      communityFlag: communityFlag || null,
    }
  }, [sellerId])

  const flagCustomer = useCallback(async ({ phone, reasonCategory, reasonDetail, orderId, isSellerFault }) => {
    if (!phone || !sellerId) return false
    const { error } = await supabase.from('customer_flags').insert({
      phone,
      flagged_by_seller_id: sellerId,
      reason_category: reasonCategory,
      reason_detail: reasonDetail || null,
      order_id: orderId || null,
      is_seller_side_return: isSellerFault || false,
      is_private: true,
      is_active: true,
    })
    return !error
  }, [sellerId])

  const unflagCustomer = useCallback(async (flagId) => {
    const { error } = await supabase
      .from('customer_flags')
      .update({ is_active: false, resolved_at: new Date().toISOString() })
      .eq('id', flagId)
      .eq('flagged_by_seller_id', sellerId)
    return !error
  }, [sellerId])

  const recordSuccessfulDelivery = useCallback(async (phone) => {
    if (!phone) return
    // Update community flag successful deliveries count
    await supabase.rpc('increment_successful_deliveries', { p_phone: phone }).catch(() => {})
  }, [])

  return { checkPhone, flagCustomer, unflagCustomer, recordSuccessfulDelivery }
}

export const FLAG_REASONS = [
  { key: 'ghost_order',        label: 'Ghost Order',         desc: 'Placed order then disappeared — no payment, no response' },
  { key: 'rejected_delivery',  label: 'Rejected Delivery',   desc: 'Courier arrived, customer refused to accept' },
  { key: 'fake_address',       label: 'Fake Address',        desc: 'Delivery failed — address does not exist or completely wrong' },
  { key: 'payment_fraud',      label: 'Payment Fraud',       desc: 'bKash/Nagad payment reversed or disputed fraudulently' },
  { key: 'customer_return',    label: 'Unjustified Return',  desc: 'Customer returned without valid reason (not seller fault)' },
  { key: 'abusive',            label: 'Abusive Behaviour',   desc: 'Rude, threatening or abusive behaviour toward seller' },
]

export const RETURN_REASONS = {
  seller: [
    { key: 'wrong_product',   label: 'Wrong product sent' },
    { key: 'defective',       label: 'Defective / damaged product' },
    { key: 'size_error',      label: 'Size mismatch (seller error)' },
    { key: 'not_as_described',label: 'Product not as described / photo mismatch' },
  ],
  customer: [
    { key: 'changed_mind',    label: 'Customer changed mind' },
    { key: 'refused_door',    label: 'Customer refused at door without reason' },
    { key: 'multiple_orders', label: 'Ordered multiple, returning all or most' },
    { key: 'fraud_claim',     label: 'Customer claims not ordered (potential fraud)' },
    { key: 'no_reason',       label: 'No reason given / unresponsive' },
  ]
}

export function getFlagLevel(communityFlag) {
  if (!communityFlag) return null
  return communityFlag.level // 'watch', 'caution', 'high_risk'
}

export const FLAG_LEVEL_CONFIG = {
  watch:     { label: 'Watch',     color: '#D97706', bg: '#FFFBEB', border: '#FDE68A',  icon: '⚑' },
  caution:   { label: 'Caution',   color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA',  icon: '⚠' },
  high_risk: { label: 'High Risk', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA',  icon: '🚫' },
}